import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, TextField, InputAdornment, FormControl,
  InputLabel, Select, MenuItem, Chip, Alert, Tab, Tabs
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Search, Warning } from '@mui/icons-material';
import { useStockStore } from '../store/stockStore';
import { stockService } from '../services/stockService';
import { almacenService } from '../../almacenes/services/almacenService';
import type { Deposito } from '../../almacenes/types/almacen.types';

const StockPage = () => {
  const { stocks, alerts, loading, searchTerm, selectedDeposito, setStocks, setAlerts, setLoading, setSearchTerm, setSelectedDeposito } = useStockStore();
  const [depositos, setDepositos] = useState<Deposito[]>([]);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    loadDepositos();
    loadStock();
    loadAlerts();
  }, []);

  useEffect(() => { loadStock(); }, [selectedDeposito]);

  const loadDepositos = async () => {
    try {
      const data = await almacenService.getAll();
      setDepositos(data);
    } catch { /* handled */ }
  };

  const loadStock = async () => {
    setLoading(true);
    try {
      const data = selectedDeposito
        ? await stockService.getAll(selectedDeposito)
        : await stockService.getAll();
      setStocks(data);
    } finally { setLoading(false); }
  };

  const loadAlerts = async () => {
    try {
      const data = await stockService.getAlerts();
      setAlerts(data);
    } catch { /* handled */ }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) { loadStock(); return; }
    setLoading(true);
    try {
      const data = await stockService.search(searchTerm);
      setStocks(data);
    } finally { setLoading(false); }
  };

  const columns: GridColDef[] = [
    { field: 'articuloCodigo', headerName: 'Código', width: 120 },
    { field: 'articuloDescripcion', headerName: 'Artículo', flex: 1, minWidth: 200 },
    { field: 'depositoNombre', headerName: 'Almacén', width: 150 },
    { field: 'unidadMedida', headerName: 'U.M.', width: 80 },
    { field: 'cantidad', headerName: 'Stock', width: 120, type: 'number',
      renderCell: (params) => {
        const val = params.value ?? 0;
        const min = params.row.stockMinimo ?? 0;
        return <Chip label={val} size="small" color={val <= min && min > 0 ? 'error' : 'default'} />;
      }
    },
    { field: 'cantidadComprometida', headerName: 'Comprometido', width: 130, type: 'number' },
    { field: 'cantidadPedida', headerName: 'Pedido', width: 100, type: 'number' },
    { field: 'stockMinimo', headerName: 'Stock Mín.', width: 110, type: 'number' },
    { field: 'puntoDePedido', headerName: 'Pto. Pedido', width: 110, type: 'number' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>Consulta de Stock</Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField size="small" placeholder="Buscar artículo..." value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          sx={{ width: 300 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Almacén</InputLabel>
          <Select value={selectedDeposito ?? ''} label="Almacén"
            onChange={(e) => setSelectedDeposito(e.target.value ? Number(e.target.value) : null)}>
            <MenuItem value="">Todos</MenuItem>
            {depositos.map((d) => <MenuItem key={d.idDeposito} value={d.idDeposito}>{d.nombre}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={`Stock (${stocks.length})`} />
        <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" fontSize="small" />Alertas ({alerts.length})
        </Box>} />
      </Tabs>

      {tabValue === 0 && (
        <Paper sx={{ height: 600 }}>
          <DataGrid rows={stocks} columns={columns} getRowId={(row) => row.idStock}
            loading={loading} density="compact"
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]} />
        </Paper>
      )}

      {tabValue === 1 && (
        <Paper sx={{ p: 2 }}>
          {alerts.length === 0 ? (
            <Alert severity="success">No hay alertas de stock bajo</Alert>
          ) : (
            alerts.map((a) => (
              <Alert key={a.idStock} severity="warning" sx={{ mb: 1 }}>
                <strong>{a.articuloCodigo}</strong> - {a.articuloDescripcion}: Stock actual <strong>{a.cantidad}</strong>, Mínimo: {a.stockMinimo}
                {a.depositoNombre && ` (${a.depositoNombre})`}
              </Alert>
            ))
          )}
        </Paper>
      )}
    </Box>
  );
};

export default StockPage;
